/**
 * BrandLogo — renders the ExpressSub wordmark image.
 *
 * Variants (choose based on the container/header background color):
 *   - "on-white"     (alias: "black")        → BlackGreen wordmark (black "Express" + green "Sub")
 *                                              use on WHITE / light backgrounds
 *   - "on-dark-blue" (alias: "white")        → WhiteGreen wordmark (white "Express" + green "Sub")
 *                                              use on DARK BLUE backgrounds (#0f1729 / bg-slate-900 / bg-slate-950)
 *   - "on-green"     (alias: "white-black")  → WhiteBlack wordmark (white "Express" + black "Sub")
 *                                              use on GREEN backgrounds (#2A956B / bg-emerald-600)
 *
 * Sizes control the pixel height; width auto-scales to preserve aspect ratio.
 */
const HEIGHTS = {
  xs: 'h-[30px]',   // 20px × 1.5
  sm: 'h-9',        // 24px × 1.5 = 36px
  md: 'h-[42px]',   // 28px × 1.5
  lg: 'h-12',       // 32px × 1.5 = 48px
  xl: 'h-[60px]',   // 40px × 1.5
  '2xl': 'h-[72px]', // 48px × 1.5
  // Public header: 32px on phones, 40px from 480px, 48px from sm up
  header: 'h-8 min-[480px]:h-10 sm:h-12',
};

const SRC = {
  // Semantic names (preferred)
  'on-white':      '/brand/BlackGreen.png',
  'on-dark-blue':  '/brand/WhiteGreen.png',
  'on-green':      '/brand/WhiteBlack.png',
  // Backward-compat aliases (existing callers)
  black:           '/brand/BlackGreen.png',
  white:           '/brand/WhiteGreen.png',
  'white-black':   '/brand/WhiteBlack.png',
};

export default function BrandLogo({
  variant = 'on-dark-blue',
  size = 'md',
  className = '',
  'data-testid': dataTestId = 'brand-logo',
}) {
  const heightClass = HEIGHTS[size] || HEIGHTS.md;
  const src = SRC[variant] || SRC['on-dark-blue'];
  return (
    <img
      src={src}
      alt="ExpressSub"
      draggable={false}
      className={`${heightClass} w-auto object-contain shrink-0 select-none ${className}`}
      data-testid={dataTestId}
    />
  );
}

/**
 * BrandMark — renders the square favicon glyph only (no wordmark).
 * Useful for tight square-icon slots (e.g., mobile menus, avatars).
 */
export function BrandMark({
  variant = 'green',
  size = 'md',
  className = '',
  'data-testid': dataTestId = 'brand-mark',
}) {
  const src =
    variant === 'black'
      ? '/brand/FaviconBlack512.png'
      : '/brand/FaviconGreen512.png';
  const heightClass = HEIGHTS[size] || HEIGHTS.md;
  return (
    <img
      src={src}
      alt="ExpressSub"
      draggable={false}
      className={`${heightClass} w-auto select-none ${className}`}
      data-testid={dataTestId}
    />
  );
}
